import React from "react";
import { Link } from "react-router-dom";
import { CustomerServiceSidebar } from "./Delivery";
import "../css/content-page.scss";
import "../css/privacy.scss";

const scrollToTop = () => {
  window.scrollTo({ top: 0, behavior: "smooth" });
};

function Privacy() {
  return (
    <main className="content-page privacy-page">
      <div className="content-page__shell">
        <CustomerServiceSidebar currentPath="/privacy" />

        <article className="content-page__article" aria-labelledby="privacy-title">
          <header className="content-page__header">
            <p className="content-page__eyebrow">Your information and choices</p>
            <h1 id="privacy-title">Privacy Policy</h1>
            <p className="content-page__lead">
              This policy explains how sickzuno handles information when you browse,
              build a cart, contact customer care or continue through checkout.
            </p>
          </header>

          <p className="content-page__meta">Last updated: October 16, 2026</p>

          <div className="content-page__notice" role="note">
            <strong>Payment privacy:</strong> the sickzuno checkout form does not ask
            for or store a full card number or card security code. Those details are
            entered on the third-party hosted payment page.
          </div>

          <section className="content-page__section" aria-labelledby="privacy-scope">
            <h2 id="privacy-scope">1. Scope and responsible brand</h2>
            <p>
              This policy applies to the sickzuno online storefront, including its
              product pages, cart, checkout, payment-result page and customer-care
              form. References to “we,” “us” and “our” mean sickzuno.
            </p>
            <p>
              A carrier, hosted payment provider or external website controls its own
              processing and provides separate privacy information for its service.
            </p>
          </section>

          <section className="content-page__section" aria-labelledby="privacy-information">
            <h2 id="privacy-information">2. Information handled by the storefront</h2>
            <div className="content-page__table-wrap">
              <table className="content-page__table">
                <caption className="content-page__visually-hidden">
                  Categories of information handled by sickzuno
                </caption>
                <thead>
                  <tr>
                    <th scope="col">Context</th>
                    <th scope="col">Information</th>
                    <th scope="col">Current behavior</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Shopping cart</td>
                    <td>Product ID, selected options, packaging and quantity</td>
                    <td>Stored in local browser storage under “cartItems”.</td>
                  </tr>
                  <tr>
                    <td>Checkout form</td>
                    <td>Name, company, email and delivery-address fields</td>
                    <td>Held in the page while checkout is open. The current front-end does not save the address fields to browser storage.</td>
                  </tr>
                  <tr>
                    <td>Payment initiation</td>
                    <td>Order reference, total, currency, email and first product title</td>
                    <td>Sent to the site payment endpoint to start the hosted payment flow.</td>
                  </tr>
                  <tr>
                    <td>Order result</td>
                    <td>Order reference, email, item count, total and creation time</td>
                    <td>Stored in session storage as “sickzuno:lastOrder”.</td>
                  </tr>
                  <tr>
                    <td>Contact form</td>
                    <td>Name, email, subject and message</td>
                    <td>Validated in the page and cleared after the on-screen confirmation; the current form does not send a network request.</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          <section className="content-page__section" aria-labelledby="privacy-automatic">
            <h2 id="privacy-automatic">3. Technical information</h2>
            <p>
              A hosting provider, network or security service may process ordinary
              request information such as IP address, browser type, device details,
              requested page, referring page, date and time, and diagnostic or security
              logs. The application code itself does not configure a dedicated analytics
              or advertising tracker.
            </p>
          </section>

          <section className="content-page__section" aria-labelledby="privacy-use">
            <h2 id="privacy-use">4. Why information is used</h2>
            <ul className="content-page__list">
              <li>Keep selected items available in the same browser.</li>
              <li>Validate checkout fields and prepare a requested payment session.</li>
              <li>Display the recent order reference and payment result.</li>
              <li>Respond to questions sent directly to the customer-care email.</li>
              <li>Protect checkout, investigate abuse and diagnose technical problems.</li>
              <li>Comply with lawful obligations and establish or defend legal claims.</li>
            </ul>
            <p>
              Where a legal basis is required, processing may be necessary to take steps
              requested before a purchase, perform a contract, meet a legal obligation,
              pursue legitimate interests such as security and customer service, or act
              on consent where the law requires consent.
            </p>
          </section>

          <section className="content-page__section" aria-labelledby="privacy-sharing">
            <h2 id="privacy-sharing">5. When information is disclosed</h2>
            <p>
              Information may be disclosed only as reasonably needed to providers that
              host or secure the site, initiate and process payment, transport an order,
              support the storefront, or comply with lawful requests. Each provider may
              process information under its own contract and privacy obligations.
            </p>
            <p>
              The current application code does not sell personal information or share
              it for cross-context behavioral advertising. If the business or ownership
              of sickzuno changes, relevant information may be transferred as part of
              that transaction subject to applicable privacy law.
            </p>
          </section>

          <section className="content-page__section" aria-labelledby="privacy-retention">
            <h2 id="privacy-retention">6. Storage and retention</h2>
            <p>
              Local cart data remains on the customer’s device until the cart or site
              data is cleared. The recent-order summary remains for the browser session
              unless cleared sooner. Information processed by payment, hosting, carrier
              or support providers may be kept for the period needed to complete the
              service, meet accounting or legal duties, prevent fraud and resolve claims.
            </p>
            <p>
              Retention periods can vary by record and jurisdiction. Information is
              deleted, anonymized or restricted when no longer reasonably needed, subject
              to technical backups and lawful retention requirements.
            </p>
          </section>

          <section className="content-page__section" aria-labelledby="privacy-transfers">
            <h2 id="privacy-transfers">7. International processing</h2>
            <p>
              Storefront and service providers may operate in countries other than the
              customer’s own. Where applicable law requires safeguards for an
              international transfer, the responsible provider or sickzuno will use an
              available lawful mechanism appropriate to the transfer.
            </p>
          </section>

          <section className="content-page__section" aria-labelledby="privacy-security">
            <h2 id="privacy-security">8. Security</h2>
            <p>
              The storefront separates card entry from the site’s checkout form and
              relies on the hosted payment page for card processing. No online service
              can guarantee absolute security. Customers should
              never send card numbers, card security codes, passwords or one-time codes
              by email or through the contact form.
            </p>
          </section>

          <section className="content-page__section" aria-labelledby="privacy-rights">
            <h2 id="privacy-rights">9. Your rights and choices</h2>
            <p>
              Depending on where you live, you may have rights to access, know, correct,
              delete or receive a copy of personal information, or to object to, restrict
              or withdraw consent for certain processing. Legal exceptions may apply.
            </p>
            <p>
              Instructions, verification details and information about sale or targeted-
              advertising choices appear on the{" "}
              <Link className="content-page__inline-link" to="/privacy-choices" onClick={scrollToTop}>
                Your Privacy Choices
              </Link>{" "}
              page.
            </p>
          </section>

          <section className="content-page__section" aria-labelledby="privacy-children">
            <h2 id="privacy-children">10. Children</h2>
            <p>
              The storefront is intended for a general adult shopping audience and is
              not directed to children. We do not knowingly ask a child to submit
              personal information. A parent or guardian who believes a child provided
              information may contact us to request review and deletion where applicable.
            </p>
          </section>

          <section className="content-page__section" aria-labelledby="privacy-links">
            <h2 id="privacy-links">11. External links</h2>
            <p>
              External sites and hosted services are not controlled by this policy.
              Review their privacy and security information before submitting personal
              information. A link does not by itself mean sickzuno endorses every
              statement or practice on the destination site.
            </p>
          </section>

          <section className="content-page__section" aria-labelledby="privacy-updates">
            <h2 id="privacy-updates">12. Changes and contact</h2>
            <p>
              This policy may be updated when the storefront, providers or legal
              requirements change. The revised version will show a new “Last updated”
              date, and additional notice will be provided when required by law.
            </p>
            <p>
              Email privacy questions or requests to{" "}
              <a className="content-page__inline-link" href="mailto:harv@sickzuno.online?subject=Privacy%20Request">
                harv@sickzuno.online
              </a>
              . Do not include payment-card details or unnecessary sensitive information.
            </p>
          </section>

          <aside className="content-page__support" aria-label="Privacy support">
            <div>
              <h2>Have a privacy question?</h2>
              <p>Email sickzuno and describe the information or choice involved.</p>
            </div>
            <Link className="content-page__inline-link" to="/privacy-choices" onClick={scrollToTop}>
              Review your choices
            </Link>
          </aside>
        </article>
      </div>
    </main>
  );
}

export default Privacy;
